import React, { useState } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from '../ui/dialog';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Textarea } from '../ui/textarea';
import { Button } from '../ui/button';
import FormPrivacyNotice from '../FormPrivacyNotice';
import { pressLogos } from '../../data/pressCoverage';
import { submitLead, normalizePhone } from '@/lib/submitLead';
import { useInfiniteMarquee } from '@/hooks/useInfiniteMarquee';
import { cn } from '@/lib/utils';

const services = [
  {
    label: 'Swim & Daycare',
    description: 'Splash sessions and supervised daytime play',
    img: 'https://customer-assets.emergentagent.com/job_petlife-hub-1/artifacts/mil495hm_F5.jpg',
  },
  {
    label: 'Dog Boarding',
    description: 'Cozy overnight stays at our boarding campus',
    img: 'https://images.unsplash.com/photo-1748355432826-6a3c421ff5bf?crop=entropy&cs=srgb&fm=jpg&q=85',
  },
  {
    label: 'Grooming Spa',
    description: 'Full grooming, bath, and styling sessions',
    img: 'https://images.unsplash.com/photo-1644675443401-ea4c14bad0e6?crop=entropy&cs=srgb&fm=jpg&q=85',
  },
  {
    label: 'Dog Walking',
    description: 'Daily walks with certified, trusted walkers',
    img: 'https://images.unsplash.com/photo-1759914915132-9878a4c702e6?crop=entropy&cs=srgb&fm=jpg&q=85',
  },
  {
    label: 'Pet Birthdays',
    description: "Celebrate your pet's special day with us",
    img: 'https://images.unsplash.com/flagged/photo-1583958211970-af3e1596ef32?q=80&w=1668&auto=format&fit=crop&ixlib=rb-4.1.0',
  },
  {
    label: 'Wag & Wine Cafe',
    description: 'Curated cafe and retail space for pet parents',
    img: '/images/wag-and-wine.png',
  },
  {
    label: 'Events',
    description: 'Meetups, workshops, and seasonal pet experiences',
    img: '/images/amphi.png',
  },
  {
    label: 'TailsClub',
    description: 'Membership perks and community for pet parents',
    img: '/images/facility-dogs-playing.jpg',
  },
];

const HeroSubline = () => {
  const [activeService, setActiveService] = useState(null);
  const [form, setForm] = useState({ name: '', email: '', phone: '', message: '' });
  const [consent, setConsent] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const { viewportRef, firstSetRef, isDragging, consumeDragClick } = useInfiniteMarquee();

  const handleServiceClick = (service) => {
    if (consumeDragClick()) return;
    setActiveService(service);
    setConsent(false);
    setForm({ name: '', email: '', phone: '', message: `I'd like to know more about ${service.label}.` });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (submitting || !activeService) return;
    setSubmitting(true);
    const result = await submitLead({
      source: 'service-inquiry',
      name: form.name.trim(),
      phone: normalizePhone(form.phone),
      email: form.email.trim(),
      service: activeService.label,
      message: form.message.trim(),
      consent,
    });
    setSubmitting(false);
    if (result?.success) setActiveService(null);
  };

  const renderServiceSet = (copyKey, { hidden } = { hidden: false }) =>
    services.map((service) => (
      <button
        key={`${copyKey}-${service.label}`}
        type="button"
        tabIndex={hidden ? -1 : undefined}
        onClick={() => handleServiceClick(service)}
        className="group relative aspect-[3/4] w-52 flex-none overflow-hidden rounded-2xl bg-neutral-100 text-left sm:w-56"
      >
        <img
          src={service.img}
          alt={hidden ? '' : service.label}
          draggable={false}
          className="pointer-events-none absolute inset-0 h-full w-full object-cover transition-transform duration-700 group-hover:scale-105"
        />
        <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-black/30 to-transparent" />
        <div className="absolute inset-x-0 bottom-0 p-4 text-white">
          <p className="text-sm font-semibold sm:text-base">{service.label}</p>
          <p className="mt-1 text-[11px] font-light text-white/85 sm:text-xs">{service.description}</p>
        </div>
      </button>
    ));

  return (
    <section id="featured-on" className="bg-white pb-14 pt-8 sm:pb-20 sm:pt-10">
      <div className="mx-auto max-w-6xl px-4 text-center">
        <p className="landing-eyebrow-muted text-sm sm:text-base">Featured On</p>
        <div className="mt-8 flex flex-wrap items-center justify-center gap-x-16 gap-y-10 sm:gap-x-24 sm:gap-y-12">
          {pressLogos.map((logo) => (
            <a
              key={logo.alt}
              href={logo.href}
              target="_blank"
              rel="noopener noreferrer"
              aria-label={`Read ${logo.alt} feature`}
              className="inline-flex transition-opacity hover:opacity-80"
            >
              <img
                src={logo.src}
                alt={logo.alt}
                className="h-9 w-auto object-contain sm:h-11 lg:h-14"
              />
            </a>
          ))}
        </div>
      </div>

      <div className="mx-auto mt-16 max-w-3xl px-4 text-center sm:mt-20">
        <p className="landing-intro-lede">
          PetCart brings together <span className="font-semibold text-primary">wellness</span>,{' '}
          <span className="font-semibold text-primary">boarding</span>,{' '}
          <span className="font-semibold text-primary">clinic</span>,{' '}
          <span className="font-semibold text-primary">retail</span> and{' '}
          <span className="font-semibold text-primary">community</span> into one seamless experience
          so you can focus on loving them.
        </p>
      </div>

      <div
        ref={viewportRef}
        role="region"
        aria-label="PetCart services"
        className={cn(
          'relative mt-10 w-full overflow-x-auto overflow-y-hidden overscroll-x-contain sm:mt-12',
          '[scrollbar-width:none] [-ms-overflow-style:none] [&::-webkit-scrollbar]:hidden',
          'select-none touch-pan-x',
          isDragging ? 'cursor-grabbing [&_button]:cursor-grabbing' : 'cursor-grab [&_button]:cursor-grab',
        )}
      >
        <div className="flex w-max gap-6">
          <div ref={firstSetRef} className="flex gap-6">
            {renderServiceSet('a')}
          </div>
          <div className="flex gap-6" aria-hidden="true">
            {renderServiceSet('b', { hidden: true })}
          </div>
        </div>
      </div>

      <Dialog open={!!activeService} onOpenChange={(open) => !open && setActiveService(null)}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle>Connect with us{activeService ? ` — ${activeService.label}` : ''}</DialogTitle>
            <DialogDescription>
              Share your details and our team will reach out to help with {activeService?.label || 'your request'}.
            </DialogDescription>
          </DialogHeader>
          <form onSubmit={handleSubmit} className="space-y-4">
            <div className="space-y-2">
              <Label htmlFor="connect-name">Name</Label>
              <Input
                id="connect-name"
                required
                value={form.name}
                onChange={(e) => setForm({ ...form, name: e.target.value })}
                placeholder="Your full name"
              />
            </div>
            <div className="grid gap-4 sm:grid-cols-2">
              <div className="space-y-2">
                <Label htmlFor="connect-email">Email</Label>
                <Input
                  id="connect-email"
                  type="email"
                  required
                  value={form.email}
                  onChange={(e) => setForm({ ...form, email: e.target.value })}
                  placeholder="you@example.com"
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="connect-phone">Phone</Label>
                <Input
                  id="connect-phone"
                  type="tel"
                  required
                  value={form.phone}
                  onChange={(e) => setForm({ ...form, phone: e.target.value })}
                  placeholder="+91 9xxxxxxxxx"
                />
              </div>
            </div>
            <div className="space-y-2">
              <Label htmlFor="connect-message">Message</Label>
              <Textarea
                id="connect-message"
                rows={3}
                maxLength={2000}
                value={form.message}
                onChange={(e) => setForm({ ...form, message: e.target.value })}
              />
            </div>
            <FormPrivacyNotice
              checked={consent}
              onChange={setConsent}
              id="service-privacy-consent"
            />
            <DialogFooter>
              <Button type="button" variant="ghost" onClick={() => setActiveService(null)} disabled={submitting}>
                Cancel
              </Button>
              <Button type="submit" disabled={submitting} className="bg-primary text-white hover:bg-primary/90 disabled:opacity-60">
                {submitting ? 'Sending…' : 'Send message'}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>
    </section>
  );
};

export default HeroSubline;
